// Hero metric strip - active, deadlines, attention, ready to file.
// Backed by GET /api/firm/counts via the `useFirmCounts` hook; skeletons
// while the aggregate is unknown - never invented numbers.
//
// A tile is a button only where a screen lists exactly what it counts;
// Critical and Needs attention have no such screen and stay plain numbers
// whose sub names the definition.

import { useNavigate } from "react-router-dom";
import { Skeleton, SkeletonGroup } from "../chrome/Skeleton";
import { useFirmCounts } from "../../hooks/useFirmCounts";
import { fmtNum } from "../../lib/money";

interface TileProps {
  tone: "navy" | "red" | "gold" | "ok";
  label: string;
  value: number;
  sub: string;
  title?: string;
  onOpen?: () => void;
}

function Tile({ tone, label, value, sub, title, onOpen }: TileProps) {
  return (
    <div
      className={`cc-metric ${tone}`}
      role={onOpen ? "button" : undefined}
      tabIndex={onOpen ? 0 : undefined}
      title={title}
      onClick={onOpen}
      onKeyDown={
        onOpen
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onOpen();
              }
            }
          : undefined
      }
    >
      <div className="label">{label}</div>
      <div className="num tnum">{fmtNum(value)}</div>
      <div className="sub">{sub}</div>
    </div>
  );
}

export function HeroMetrics() {
  const counts = useFirmCounts();
  const navigate = useNavigate();

  if (!counts) {
    return (
      <div className="cc-metrics">
        <SkeletonGroup label="Loading metrics">
          <Skeleton width="100%" height={92} radius={10} />
        </SkeletonGroup>
        <Skeleton width="100%" height={92} radius={10} />
        <Skeleton width="100%" height={92} radius={10} />
        <Skeleton width="100%" height={92} radius={10} />
      </div>
    );
  }

  return (
    <div className="cc-metrics">
      <Tile
        tone="navy"
        label="Active matters"
        value={counts.activeMatters}
        sub="active and pending; on hold and unreviewed leads not counted"
        title="Open the cases list"
        onOpen={() => navigate("/cases")}
      />
      <Tile
        tone="red"
        label="Critical deadlines"
        value={counts.deadlinesCritical}
        sub={`${fmtNum(counts.deadlinesOverdue)} overdue; due within 3 days or flagged critical; ${fmtNum(counts.deadlinesThisWeek)} due within 7 days`}
      />
      <Tile
        tone="gold"
        label="Needs attention"
        value={counts.needsAttention}
        sub="missing info, conflicts, or urgent"
      />
      <Tile
        tone="ok"
        label="Ready to file"
        value={counts.readyToFile}
        sub="Filing queue - open, filing stage, nothing missing"
        title="Open the filing queue"
        onOpen={() => navigate("/filings")}
      />
    </div>
  );
}
